import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Block as BlockIcon,
  PlayArrow as PlayArrowIcon,
  Delete as DeleteIcon,
  Storage as StorageIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import api from '../../../services/api';
import { grey, primary, success, warning, error as errorColor, info } from '../../../styles/tokens';

/**
 * Detail d'un Coach (Admin)
 */
const CoachDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Dialogs
  const [suspendDialog, setSuspendDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);

  const fetchCoach = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/admin/coaches/${id}`);
      setCoach(response.coach || response);
    } catch (err) {
      console.error('Erreur lors du chargement du coach:', err);
      setError('Impossible de charger les details du coach.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoach();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSuspend = async () => {
    try {
      setActionLoading(true);
      await api.post(`/admin/coaches/${id}/suspend`);
      setSuspendDialog(false);
      fetchCoach();
    } catch (err) {
      console.error('Erreur lors de la suspension:', err);
      setError('Impossible de suspendre le coach.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setActionLoading(true);
      await api.post(`/admin/coaches/${id}/reactivate`);
      fetchCoach();
    } catch (err) {
      console.error('Erreur lors de la reactivation:', err);
      setError('Impossible de reactiver le coach.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setActionLoading(true);
      await api.delete(`/admin/coaches/${id}`);
      setDeleteDialog(false);
      navigate('/admin/coaches');
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError('Impossible de supprimer le coach.');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusChipProps = (status) => {
    const configs = {
      active: { label: 'Actif', sx: { bgcolor: success[50], color: success.dark } },
      suspended: { label: 'Suspendu', sx: { bgcolor: errorColor[50], color: errorColor.dark } },
      pending: { label: 'En attente', sx: { bgcolor: warning[50], color: warning.dark } },
      provisioning: { label: 'Provisioning', sx: { bgcolor: info[50], color: info.dark } },
    };
    return configs[status] || { label: status || 'N/A', sx: { bgcolor: grey[100], color: grey[600] } };
  };

  const getPlanChipProps = (plan) => {
    const configs = {
      starter: { label: 'Starter', sx: { bgcolor: grey[100], color: grey[700] } },
      pro: { label: 'Pro', sx: { bgcolor: primary[50], color: primary[600] } },
      enterprise: { label: 'Enterprise', sx: { bgcolor: success[50], color: success.dark } },
    };
    return configs[plan] || { label: plan || 'N/A', sx: { bgcolor: grey[100], color: grey[600] } };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error && !coach) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
          onClick={() => navigate('/admin/coaches')}
          sx={{ mb: 3 }}
        >
          Retour
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const statusProps = getStatusChipProps(coach?.tenantStatus);
  const planProps = getPlanChipProps(coach?.subscriptionPlan);

  return (
    <>
      <Helmet>
        <title>{coach?.firstName} {coach?.lastName} - Admin Yoco</title>
      </Helmet>

      <Box>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <IconButton onClick={() => navigate('/admin/coaches')}>
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <Typography variant="h1">
            Detail du Coach
          </Typography>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Profile Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: primary[100],
                  color: primary[600],
                  fontSize: '1.5rem',
                  fontWeight: 600,
                }}
              >
                {coach?.firstName?.[0]}{coach?.lastName?.[0]}
              </Avatar>

              <Box sx={{ flex: 1 }}>
                <Typography variant="h2" sx={{ mb: 1 }}>
                  {coach?.firstName} {coach?.lastName}
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Chip label={planProps.label} size="small" sx={planProps.sx} />
                  <Chip label={statusProps.label} size="small" sx={statusProps.sx} />
                </Stack>

                <Stack direction="row" spacing={3} flexWrap="wrap">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <EmailIcon sx={{ fontSize: 18, color: grey[400] }} />
                    <Typography variant="body2">{coach?.email}</Typography>
                  </Stack>
                  {coach?.phone && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <PhoneIcon sx={{ fontSize: 18, color: grey[400] }} />
                      <Typography variant="body2">{coach?.phone}</Typography>
                    </Stack>
                  )}
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarIcon sx={{ fontSize: 18, color: grey[400] }} />
                    <Typography variant="body2">
                      Inscrit le {new Date(coach?.createdAt).toLocaleDateString('fr-FR')}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              {/* Actions */}
              <Stack direction="row" spacing={1}>
                {coach?.tenantStatus === 'active' ? (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<BlockIcon sx={{ fontSize: 18 }} />}
                    onClick={() => setSuspendDialog(true)}
                    disabled={actionLoading}
                  >
                    Suspendre
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<PlayArrowIcon sx={{ fontSize: 18 }} />}
                    onClick={handleReactivate}
                    disabled={actionLoading}
                  >
                    Reactiver
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon sx={{ fontSize: 18 }} />}
                  onClick={() => setDeleteDialog(true)}
                  disabled={actionLoading}
                >
                  Supprimer
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Stats */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: primary[50], width: 40, height: 40 }}>
                  <PeopleIcon sx={{ color: primary[500], fontSize: 20 }} />
                </Avatar>
                <Box>
                  <Typography variant="h2">
                    {coach?.currentClients || 0} / {coach?.maxClients || '-'}
                  </Typography>
                  <Typography variant="caption">Clients</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: info[50], width: 40, height: 40 }}>
                  <StorageIcon sx={{ color: info.main, fontSize: 20 }} />
                </Avatar>
                <Box>
                  <Typography
                    variant="body1"
                    sx={{ fontFamily: 'monospace', fontWeight: 500 }}
                  >
                    {coach?.tenantId || 'N/A'}
                  </Typography>
                  <Typography variant="caption">Tenant ID</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        {/* More Info */}
        <Card>
          <CardContent>
            <Typography variant="h3" sx={{ mb: 2 }}>
              Informations supplementaires
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">ID MongoDB</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {coach?._id}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Role</Typography>
                <Typography variant="body1">{coach?.role}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Abonnement actif</Typography>
                <Typography variant="body1">
                  {coach?.isSubscribed ? 'Oui' : 'Non'}
                </Typography>
              </Stack>
              {coach?.subscriptionStartDate && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Debut abonnement</Typography>
                  <Typography variant="body1">
                    {new Date(coach.subscriptionStartDate).toLocaleDateString('fr-FR')}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Dialog Suspension */}
        <Dialog open={suspendDialog} onClose={() => setSuspendDialog(false)}>
          <DialogTitle>Suspendre le coach ?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Etes-vous sur de vouloir suspendre <strong>{coach?.firstName} {coach?.lastName}</strong> ?
              <br /><br />
              Le coach ne pourra plus acceder a son espace et ses clients ne pourront plus consulter leurs programmes.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSuspendDialog(false)} variant="outlined">
              Annuler
            </Button>
            <Button onClick={handleSuspend} color="warning" variant="contained" disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={20} /> : 'Suspendre'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog Suppression */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
          <DialogTitle>Supprimer le coach ?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Etes-vous sur de vouloir supprimer definitivement <strong>{coach?.firstName} {coach?.lastName}</strong> ?
              <br /><br />
              <strong>Cette action est irreversible.</strong> Toutes les donnees du coach et de ses clients seront perdues.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(false)} variant="outlined">
              Annuler
            </Button>
            <Button onClick={handleDelete} color="error" variant="contained" disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={20} /> : 'Supprimer'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default CoachDetail;
